export type TiendaPerfectaDistributorTier = 'top' | 'medium' | 'low';

export interface TiendaPerfectaCompany {
  code: string;
  name: string;
  description: string | null;
  countryCode: string;
}

export interface TiendaPerfectaDistributor {
  id: string;
  companyCode: string;
  code: string;
  legalName: string;
  tradeName: string;
  active: boolean;
  createdAt: string;
  /** Solo lo publica `/distributors/scoped`. */
  zone?: { id: string; name: string } | null;
}

export interface TiendaPerfectaScopeGrant {
  companyCode: string;
  fullAccess: boolean;
  distributorIds: string[];
}

/**
 * Enum del backend. Se programa siempre contra `value`, que es lo estable;
 * `label` es texto de presentación y puede cambiar sin aviso.
 */
export interface TiendaPerfectaLabeledValue {
  value: string;
  label: string;
}

export interface TiendaPerfectaDocumentType {
  code: string;
  name: string;
}

/** Los cinco documentos del expediente de un tripulante, en orden de UI. */
export const TIENDA_PERFECTA_DOCUMENT_SLOTS = [
  'photo',
  'docFront',
  'docBack',
  'criminalRecord',
  'license',
] as const;

export type TiendaPerfectaDocumentSlot =
  typeof TIENDA_PERFECTA_DOCUMENT_SLOTS[number];

/** En el listado los documentos son solo presencia: no hay URLs. */
export type TiendaPerfectaCrewDocuments = Record<
  TiendaPerfectaDocumentSlot,
  boolean
>;

export interface TiendaPerfectaCrewMember {
  operatorId: string;
  fullName: string;
  documentType: TiendaPerfectaDocumentType;
  documentNumber: string;
  dialCode: string;
  phoneNumber: string;
  fieldRole: TiendaPerfectaLabeledValue;
  licenseExpiry: string | null;
  documents: TiendaPerfectaCrewDocuments;
}

export interface TiendaPerfectaVanDistributor {
  id: string;
  name: string;
}

export interface TiendaPerfectaVan {
  id: string;
  plate: string;
  /** El nombre operativo con el que se llama a la unidad, distinto de la patente. */
  name: string;
  brandModel: string;
  /** Llega como string desde el backend. */
  volumeM3: string;
  weightKg: number;
  estado: TiendaPerfectaLabeledValue;
  photoUrl: string | null;
  /** Una camioneta puede servir a varios distribuidores. */
  distributors: TiendaPerfectaVanDistributor[];
  /** Cada puesto es nullable por separado. */
  crew: {
    conductor: TiendaPerfectaCrewMember | null;
    peoneta: TiendaPerfectaCrewMember | null;
  };
}

export interface TiendaPerfectaVansResponse {
  vans: TiendaPerfectaVan[];
}

export interface TiendaPerfectaVansParams {
  distributorId: string;
  /** Acá habla del estado de la CAMIONETA, no de la vigencia del distribuidor. */
  includeInactive?: boolean;
}

/**
 * `url` puede ser `null` con `present: true`: la URL firmada no se pudo emitir
 * en ese momento. `present` es existencia; `url` es disponibilidad ahora mismo.
 */
export interface TiendaPerfectaOperatorSlot {
  present: boolean;
  url: string | null;
}

/** A diferencia del resto del módulo, la respuesta es un objeto plano. */
export interface TiendaPerfectaOperatorDetail {
  operatorId: string;
  fullName: string;
  documentType: TiendaPerfectaDocumentType;
  documentNumber: string;
  dialCode: string;
  phoneNumber: string;
  fieldRole: TiendaPerfectaLabeledValue;
  licenseExpiry: string | null;
  slots: Record<TiendaPerfectaDocumentSlot, TiendaPerfectaOperatorSlot>;
  /** Todas las que tripula hoy, incluidas las fuera de tu alcance. */
  plates: string[];
}

export interface TiendaPerfectaMyScopeResponse {
  companies: TiendaPerfectaScopeGrant[];
}

export interface TiendaPerfectaCompaniesResponse {
  companies: TiendaPerfectaCompany[];
}

export interface TiendaPerfectaDistributorsResponse {
  distributors: TiendaPerfectaDistributor[];
}

export type TiendaPerfectaDistributorSortBy =
  | 'tradeName'
  | 'legalName'
  | 'createdAt';

export type TiendaPerfectaSortDir = 'asc' | 'desc';

export interface TiendaPerfectaDistributorsParams {
  companyCode: string;
  includeInactive?: boolean;
  sortBy?: TiendaPerfectaDistributorSortBy;
  sortDir?: TiendaPerfectaSortDir;
}

export interface TiendaPerfectaAllDistributorsParams {
  includeInactive?: boolean;
  includeInactiveCompanies?: boolean;
  sortBy?: TiendaPerfectaDistributorSortBy;
  sortDir?: TiendaPerfectaSortDir;
}

/** Sin `companyCode`: el alcance lo resuelve el servidor. */
export interface TiendaPerfectaScopedDistributorsParams {
  includeInactive?: boolean;
  sortBy?: TiendaPerfectaDistributorSortBy;
  sortDir?: TiendaPerfectaSortDir;
}

/* ────────────────────────── Reporte de rutas ──────────────────────────
   `GET /route-report`. Una sola lectura contesta el día entero.          */

/**
 * `windowStart`/`windowEnd` vienen en el timezone de la company, igual que la
 * ventana operativa. `serverTime` es el ÚNICO instante ya convertido, con su
 * offset local; el resto de los instantes del reporte viajan en UTC.
 */
export interface TiendaPerfectaRouteReportContext {
  date: string;
  timezone: string;
  windowStart: string;
  windowEnd: string;
  serverTime: string;
}

/**
 * `unitsOnRoute` y `unitsNotStarted` son complementarios y SIEMPRE suman
 * `routes.length`: una ruta ya cerrada sigue contando como "en ruta".
 */
export interface TiendaPerfectaRouteReportKpis {
  unitsOnRoute: number;
  unitsNotStarted: number;
  scheduledStops: number;
  completedStops: number;
  /**
   * Camionetas (`van.id` distintos) con al menos una ruta abierta ahora: con
   * check-in y sin cerrar, aunque no haya iniciado ninguna parada. Opcional:
   * el BFF puede publicarse antes que tienda-perfecta. Ausente = sin dato.
   */
  vansOnRoute?: number;
  /**
   * Camionetas con alguna ruta sin check-in y ninguna ruta abierta. Una
   * camioneta nunca cuenta en `vansOnRoute` y aquí a la vez. Opcional.
   */
  vansNotStarted?: number;
  /**
   * Paradas finalizadas SIN bypass (<= `completedStops`, que las cuenta con y
   * sin bypass). Opcional: ausente = sin dato.
   */
  successfulStops?: number;
}

/**
 * `projection` es `null` para cualquier día que no sea hoy en el timezone de la
 * company, y también para un día sin plan. `realProgress` siempre viene.
 */
export interface TiendaPerfectaRouteReportProgress {
  realProgress: number;
  projection: number | null;
}

/** Una parada cerrada por excepción: ya cuenta en `completedStops`. */
export interface TiendaPerfectaStopBypass {
  code: string;
  reason: string;
  /**
   * Comentario libre de quien reportó la incidencia. No está en la
   * documentación del BFF pero el backend lo envía; se trata como opcional para
   * no romper si deja de venir.
   */
  note?: string | null;
}

/**
 * Una parada sin ejecución igual viaja como marcador de posición: `id` en
 * `null`, `status: 'pending'` y sin instantes. `label` puede ser `null`: caer a
 * `code`, que nunca falta.
 */
export interface TiendaPerfectaRouteStop {
  id: string | null;
  sequence: number;
  code: string;
  label: string | null;
  status: string;
  startedAt: string | null;
  finishedAt: string | null;
  bypass: TiendaPerfectaStopBypass | null;
}

export interface TiendaPerfectaReportRoute {
  routeId: string;
  code: string;
  /**
   * `zone` es el nombre de la zona del distribuidor VIGENTE de la ruta y es
   * opcional por dos motivos que en la práctica significan lo mismo, "sin
   * zona": llega `""` cuando el distribuidor apunta a una zona de OTRA
   * company (la API no expone el nombre de una zona ajena), y llega AUSENTE
   * mientras el backend no mergee su rama `client-portal-distributor-zone`.
   * Se chequea siempre por falsy, nunca `zone === ''`.
   */
  distributor: { id: string; tradeName: string; zone?: string } | null;
  /**
   * `name` es el nombre operativo con el que se llama a la unidad y `plate` su
   * patente: son dos campos distintos y los dos viajan en el reporte.
   */
  van: { id: string; plate: string; name: string } | null;
  crew: { conductor: string | null; peoneta: string | null };
  /**
   * Lo definen las PARADAS, nunca el check-in: una ruta con check-in hecho y
   * ninguna parada iniciada es `not_started` aunque `startedAt` no sea nulo.
   */
  status: string;
  startedAt: string | null;
  finishedAt: string | null;
  stopCount: number;
  finishedStopCount: number;
  stops: TiendaPerfectaRouteStop[];
}

export interface TiendaPerfectaRouteReport {
  context: TiendaPerfectaRouteReportContext;
  kpis: TiendaPerfectaRouteReportKpis;
  progress: TiendaPerfectaRouteReportProgress;
  /** Entero, no lista: cuántas rutas quedaron fuera por datos incompletos. */
  excludedRoutes: number;
  /** Ya viene en orden de grilla: el front no reordena. */
  routes: TiendaPerfectaReportRoute[];
}

export interface TiendaPerfectaRouteReportParams {
  /** `AAAA-MM-DD`. Sin default de "hoy": omitirlo es `400`. */
  date: string;
  /**
   * Recorta el reporte a un distribuidor. Omitirlo devuelve todo el alcance de
   * la sesión. El servidor recalcula también `kpis` y `progress`, así que la
   * pantalla entera habla del mismo recorte.
   */
  distributorId?: string;
}

/* ────────────────────── Detalle de una parada ──────────────────────
   `GET /stops/{id}`. El `{id}` es el mismo `routes[].stops[].id` que publica
   el reporte; una parada con `id` en `null` no tiene detalle que abrir.       */

/**
 * Un par de coordenadas. Los tres números llegan como STRING, decimales
 * incluidos, para no perder precisión en el camino: parsealos donde los pintes,
 * nunca para compararlos.
 */
export interface TiendaPerfectaStopCoordinate {
  lat: string;
  lng: string;
  accuracyM: string | null;
}

/**
 * Los tres puntos de interés de una parada. `planned` es el punto de referencia
 * del local y se publica aunque nadie la haya visitado; `start` y `finish` son
 * las lecturas reales de la ejecución.
 */
export interface TiendaPerfectaStopCoordinates {
  planned: TiendaPerfectaStopCoordinate | null;
  start: TiendaPerfectaStopCoordinate | null;
  finish: TiendaPerfectaStopCoordinate | null;
}

/** Enum cerrado. Acá NO hay `bypass`: la excepción vive en `incident`. */
export type TiendaPerfectaStopDetailStatus =
  | 'pending'
  | 'in_progress'
  | 'finished';

/** Decide qué campos del step vienen llenos y cuáles en `null`. */
export type TiendaPerfectaFormStepType =
  | 'photo'
  | 'materials'
  | 'list'
  | 'text';

/** Sólo tiene valor en los steps de tipo `list`. */
export type TiendaPerfectaSelectionMode = 'single' | 'multiple';

/**
 * Una foto del payload, en cualquier lugar donde aparezca.
 *
 * `present: true` con `url: null` significa "la firma falló ahora", NUNCA "no
 * hay foto": `present` es existencia y `url` es disponibilidad en este momento.
 * Las URLs se firman por petición y caducan en una hora, así que la respuesta
 * no se cachea: se guarda el `stopId` y se vuelve a pedir.
 */
export interface TiendaPerfectaStopPhoto {
  present: boolean;
  url: string | null;
  position: number;
}

/** Una opción elegida en un step de tipo `list`. */
export interface TiendaPerfectaStepSelection {
  itemTitle: string;
}

/**
 * La respuesta de un paso. Es `null` cuando el paso quedó sin contestar, lo que
 * NO es lo mismo que "el paso no existe": una parada pendiente publica su
 * plantilla completa con todos los `answer` en `null`, y una parada ya cerrada
 * puede tener algún paso suelto sin responder.
 */
export interface TiendaPerfectaStepAnswer {
  textValue: string | null;
  otherValue: string | null;
  selections: TiendaPerfectaStepSelection[];
  photos: TiendaPerfectaStopPhoto[];
}

/**
 * Un paso de la plantilla congelada. Los campos son nullables porque cada
 * `type` llena sólo los suyos: un step `text` trae `placeholder` y sus cotas de
 * foto en `null`. La ausencia de un campo es el tipo del step, no un dato que
 * falte.
 */
export interface TiendaPerfectaFormStep {
  id: string;
  position: number;
  type: string;
  label: string;
  required: boolean | null;
  photoLabel: string | null;
  minPhotoCount: number | null;
  maxPhotoCount: number | null;
  optionListTitle: string | null;
  selectionMode: string | null;
  allowOther: boolean | null;
  placeholder: string | null;
  answer: TiendaPerfectaStepAnswer | null;
}

/**
 * El formulario POP de la parada. `steps` sale de la plantilla congelada, así
 * que viene completo aunque nadie lo haya respondido. Viaja en el orden de
 * `position` y no hay que reordenarlo.
 */
export interface TiendaPerfectaStopForm {
  templateName: string | null;
  steps: TiendaPerfectaFormStep[];
}

/** De dónde salió la línea de material. */
export type TiendaPerfectaMaterialSource = 'package' | 'loose';

/**
 * De dónde salió la etiqueta. `catalog` marca la fila cuyo nombre tuvo que
 * leerse del catálogo VIVO porque no había snapshot —típicamente un material
 * sugerido que nunca llegó a cargarse en la camioneta—, así que refleja el
 * nombre de hoy y no el del día de la ejecución.
 */
export type TiendaPerfectaMaterialLabelSource = 'frozen' | 'catalog';

/** Lo común a las dos listas de materiales. */
interface TiendaPerfectaMaterialLineBase {
  materialId: string;
  materialSku: string;
  materialName: string;
  sourceType: string;
  packageId: string | null;
  packageSku: string | null;
  packageName: string | null;
  labelSource: string;
}

export interface TiendaPerfectaSuggestedMaterial
  extends TiendaPerfectaMaterialLineBase {
  packageQuantity: number | null;
  recipeQuantity: number | null;
  suggestedQuantity: number;
}

export interface TiendaPerfectaDeliveredMaterial
  extends TiendaPerfectaMaterialLineBase {
  quantity: number;
}

/**
 * Dos arreglos SEPARADOS que la API no cruza. Vienen en el mismo orden —lo que
 * llegó en paquete primero, con las líneas de un mismo paquete juntas, y los
 * sueltos al final— justamente para leerse uno al lado del otro.
 *
 * Lo sugerido no es una meta: en terreno se puede dejar más, menos, o material
 * que nadie sugirió.
 */
export interface TiendaPerfectaStopMaterials {
  suggested: TiendaPerfectaSuggestedMaterial[];
  delivered: TiendaPerfectaDeliveredMaterial[];
}

/**
 * La incidencia de la parada, o `null` si no tuvo.
 *
 * `allowsBypass` y `usedForBypass` no son lo mismo: el primero es una propiedad
 * del CATÁLOGO —qué se podría hacer— y el segundo es lo que PASÓ en esta
 * ejecución. Es `usedForBypass` el que se corresponde con el `bypass` no nulo
 * que el reporte publica para la misma parada.
 */
export interface TiendaPerfectaStopIncident {
  id: string;
  code: string;
  reason: string;
  note: string | null;
  allowsBypass: boolean;
  requiresPhoto: boolean;
  markedAt: string;
  usedForBypass: boolean;
  photos: TiendaPerfectaStopPhoto[];
}

export interface TiendaPerfectaStopSummary {
  id: string;
  sequence: number;
  code: string;
  label: string | null;
  /**
   * El identificador de la sucursal en el sistema del cliente.
   *
   * La UI NO lo muestra por decisión de producto: el detalle ya se encabeza con
   * `code`, y dos identificadores del mismo local juntos se leen como un error
   * antes que como un dato. Se mantiene en el modelo porque el contrato lo
   * publica y puede pedirse más adelante.
   */
  branchExternalId: string | null;
  status: string;
  startedAt: string | null;
  finishedAt: string | null;
  coordinates: TiendaPerfectaStopCoordinates;
}

/** Un par direccionable, no el objeto completo de `/vans`. */
export interface TiendaPerfectaStopCrewMember {
  id: string;
  fullName: string;
}

/**
 * El detalle completo de una parada.
 *
 * A diferencia del resto del módulo el primer nivel NO son los campos de la
 * entidad sino ocho BLOQUES, y la parada es uno de ellos (`stop`), junto a su
 * ruta, su camioneta y el resto del contexto.
 *
 * `distributor` es la copia CONGELADA al check-in —quién era responsable ese
 * día—, no el asignado vigente, y puede ser `null` sin que la parada deje de
 * ser alcanzable.
 */
export interface TiendaPerfectaStopDetail {
  stop: TiendaPerfectaStopSummary;
  route: { id: string; code: string };
  distributor: { id: string; tradeName: string } | null;
  van: { id: string; plate: string; name: string } | null;
  crew: {
    /** Quien abrió la ejecución: siempre viene. */
    peoneta: TiendaPerfectaStopCrewMember | null;
    conductor: TiendaPerfectaStopCrewMember | null;
  };
  form: TiendaPerfectaStopForm;
  materials: TiendaPerfectaStopMaterials;
  incident: TiendaPerfectaStopIncident | null;
}

/* ─────────────────────── Historial de visitas ───────────────────────
   `GET /visit-history/summary` y `GET /visit-history/stops`.            */

export interface TiendaPerfectaVisitHistoryParams {
  /** `AAAA-MM-DD`. */
  from: string;
  /** `AAAA-MM-DD`, máximo 31 días desde `from` (inclusive). */
  to: string;
  /** Vacío o ausente = sin filtro. */
  zoneIds?: string[];
  distributorIds?: string[];
}

export interface TiendaPerfectaVisitHistoryContext {
  from: string;
  to: string;
  timezone: string;
  /** The final backend contract does not send it. */
  serverTime?: string;
}

export interface TiendaPerfectaVisitHistoryNamed {
  id: string;
  name: string;
}

export interface TiendaPerfectaVisitHistoryVan {
  id: string;
  name: string;
  plate: string;
}

export interface TiendaPerfectaVisitHistoryKpis {
  planned: number;
  executed: number;
  incidents: number;
  pending: number;
  compliancePct: number;
  routes: number;
  materialUnits: number;
}

export interface TiendaPerfectaVisitHistoryDay {
  date: string;
  planned: number;
  executed: number;
  incidents: number;
  pending: number;
  routes: number;
}

export interface TiendaPerfectaVisitHistoryDayRoute {
  routeId: string;
  routeCode: string;
  zone: TiendaPerfectaVisitHistoryNamed;
  distributor: { id: string; tradeName: string };
  van: TiendaPerfectaVisitHistoryVan;
  planned: number;
  executed: number;
  incidents: number;
}

export interface TiendaPerfectaVisitHistoryRoutesByDay {
  date: string;
  routes: TiendaPerfectaVisitHistoryDayRoute[];
}

export interface TiendaPerfectaVisitHistoryMaterials {
  catalog: { materialId: string; sku: string; name: string }[];
  totals: { materialId: string; units: number }[];
  byDay: { date: string; materialId: string; units: number }[];
  byVan: { vanId: string; materialId: string; units: number }[];
  byZone: {
    zoneId: string;
    /** `''` when the backend has no name for the zone. */
    zoneName: string;
    materialId: string;
    units: number;
  }[];
}

export interface TiendaPerfectaVisitHistoryExecution {
  routeId: string;
  routeCode: string;
  date: string;
  zone: TiendaPerfectaVisitHistoryNamed;
  planned: number;
  executed: number;
  incidents: number;
  pending: number;
  /** Not carried per execution by the final contract: null, never a fake 0. */
  compliancePct: number | null;
  materialUnits: number | null;
  startedAt: string | null;
  finishedAt: string | null;
  durationMinutes: number | null;
  visitsPerHour: number | null;
}

export interface TiendaPerfectaVisitHistoryVanRow {
  van: TiendaPerfectaVisitHistoryVan;
  zones: TiendaPerfectaVisitHistoryNamed[];
  routes: number;
  days: number;
  planned: number;
  executed: number;
  incidents: number;
  pending: number;
  compliancePct: number;
  materialUnits: number;
  /** `HH:mm` en el timezone del contexto. */
  avgStartTime: string | null;
  avgEndTime: string | null;
  avgDurationMinutes: number | null;
  visitsPerHour: number | null;
  executions: TiendaPerfectaVisitHistoryExecution[];
}

export interface TiendaPerfectaVisitHistorySummary {
  context: TiendaPerfectaVisitHistoryContext;
  kpis: TiendaPerfectaVisitHistoryKpis;
  excludedRoutes: number;
  byDay: TiendaPerfectaVisitHistoryDay[];
  routesByDay: TiendaPerfectaVisitHistoryRoutesByDay[];
  materials: TiendaPerfectaVisitHistoryMaterials;
  vans: TiendaPerfectaVisitHistoryVanRow[];
}

export type TiendaPerfectaVisitHistoryStopStatus =
  | 'pending'
  | 'in_progress'
  | 'finished';

export type TiendaPerfectaVisitHistoryOutcome =
  | 'executed'
  | 'incident'
  | 'pending';

export interface TiendaPerfectaVisitHistoryStop {
  stopId: string;
  stopExecutionId: string | null;
  plannedDate: string;
  sequence: number;
  status: TiendaPerfectaVisitHistoryStopStatus;
  outcome: TiendaPerfectaVisitHistoryOutcome;
  incident: { code: string; reason: string; note: string | null } | null;
  startedAt: string | null;
  finishedAt: string | null;
  location: { lat: number; lng: number; accuracyM: number | null } | null;
  route: { id: string; code: string };
  zone: TiendaPerfectaVisitHistoryNamed;
  distributor: { id: string; tradeName: string };
  van: TiendaPerfectaVisitHistoryVan;
  store: {
    label: string | null;
    code: string;
    branchExternalId: string | null;
    name: string | null;
    address: string | null;
    city: string | null;
  };
  materials: {
    materialId: string;
    name: string;
    sku?: string | null;
    units: number;
  }[];
}

export interface TiendaPerfectaVisitHistoryStopsResponse {
  context: TiendaPerfectaVisitHistoryContext;
  stops: TiendaPerfectaVisitHistoryStop[];
}

/* ─────── Historial de visitas: shape final del backend (crudo) ───────
   Lo que devuelve el BFF tal cual. `adaptVisitHistory*` lo lleva al modelo
   interno de arriba; todo campo no garantizado es opcional a propósito.   */

export interface RawVisitHistoryContext {
  timezone: string;
  from: string;
  to: string;
}

export interface RawVisitHistoryMaterial {
  materialId: string;
  sku?: string;
  name?: string;
  quantity: number;
}

export interface RawVisitHistoryStop {
  /** Id of the stop EXECUTION; null while the route has not started. */
  stopId: string | null;
  /** Always present: the stable key of the planned stop. */
  routeStopId: string;
  plannedDate: string;
  sequence: number;
  status: TiendaPerfectaVisitHistoryStopStatus;
  incident?: { code: string; reason: string } | null;
  startedAt?: string | null;
  finishedAt?: string | null;
  /** Finish coordinates, only for finished stops (incidents included). */
  lat?: number | null;
  lng?: number | null;
  routeId: string;
  routeCode: string;
  zone?: { id: string; name?: string } | null;
  distributor: { id: string; tradeName: string };
  van: TiendaPerfectaVisitHistoryVan;
  local?: {
    code: string;
    label?: string | null;
    branchExternalId?: string | null;
    address?: string | null;
  } | null;
  materials?: RawVisitHistoryMaterial[];
}

export interface RawVisitHistoryStopsResponse {
  context: RawVisitHistoryContext;
  stops: RawVisitHistoryStop[];
}

export interface RawVisitHistoryKpis
  extends Partial<TiendaPerfectaVisitHistoryKpis> {
  excludedRoutes?: number;
}

export interface RawVisitHistoryRouteDay {
  date: string;
  routeId: string;
  routeCode: string;
  zone?: { id: string; name?: string } | null;
  distributor: { id: string; tradeName: string };
  van: TiendaPerfectaVisitHistoryVan;
  planned?: number;
  executed?: number;
  incidents?: number;
  pending?: number;
}

export interface RawVisitHistoryMaterials {
  totals?: RawVisitHistoryMaterial[];
  byDay?: (RawVisitHistoryMaterial & { date: string })[];
  byVan?: (RawVisitHistoryMaterial & {
    van: TiendaPerfectaVisitHistoryVan;
  })[];
  byZone?: (RawVisitHistoryMaterial & {
    zone: { id: string; name?: string };
  })[];
}

export interface RawVisitHistoryExecution {
  routeExecutionId?: string;
  routeId: string;
  routeCode: string;
  plannedDate: string;
  startedAt?: string | null;
  finishedAt?: string | null;
  durationMinutes?: number | null;
}

export interface RawVisitHistoryVanRow
  extends Partial<
    Omit<TiendaPerfectaVisitHistoryVanRow, 'van' | 'executions'>
  > {
  van: TiendaPerfectaVisitHistoryVan;
  distributors?: {
    id: string;
    tradeName: string;
    zone?: { id: string; name?: string } | null;
  }[];
  executions?: RawVisitHistoryExecution[];
}

export interface RawVisitHistorySummary {
  context: RawVisitHistoryContext;
  kpis?: RawVisitHistoryKpis;
  /** The real backend does not send `routes`: the adapter computes it. */
  byDay?: (Omit<TiendaPerfectaVisitHistoryDay, 'routes'> & {
    routes?: number;
  })[];
  routesByDay?: RawVisitHistoryRouteDay[];
  materials?: RawVisitHistoryMaterials;
  vans?: RawVisitHistoryVanRow[];
}
